import { Link } from 'react-router-dom';
import SEO from '@/components/seo/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, GitCompare } from 'lucide-react';
import { COMPETITORS } from '@/data/competitors';

const SITE = 'https://getbitsy.ai';

export default function CompareIndexPage() {
  const list = Object.values(COMPETITORS);

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'getbitsy.ai vs traditional hotel software',
    description: 'Side-by-side comparisons of getbitsy.ai (Agentic Hospitality OS) against the largest OTAs and PMS platforms — Booking.com, Expedia, Mews, Cloudbeds.',
    numberOfItems: list.length,
    itemListElement: list.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE}/compare/${c.slug}`,
      name: `getbitsy.ai vs ${c.name}`,
      description: c.tagline_ours,
    })),
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100" data-testid="compare-index-page">
      <SEO
        title="getbitsy.ai vs Booking.com, Mews, Cloudbeds, Expedia — Comparisons"
        description="Side-by-side comparisons of getbitsy.ai vs traditional hotel SaaS. Direct booking engine, AI agents, and on-chain rewards versus 15-25% OTA commissions and per-room PMS subscriptions."
        canonical={`${SITE}/compare`}
        schema={itemListSchema}
      />

      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">← getbitsy.ai</Link>
        <Link to="/why-getbitsy" className="text-xs tracking-widest text-zinc-500 hover:text-zinc-300">ROI calculator</Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16 space-y-12">
        <section>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 mb-4">COMPARISONS</Badge>
          <h1 className="text-5xl font-semibold leading-tight">
            getbitsy.ai vs <span className="text-amber-400">the incumbents</span>.
          </h1>
          <p className="text-lg text-zinc-400 mt-4 max-w-3xl leading-relaxed">
            Honest head-to-head comparisons against the OTAs and PMS platforms operators are
            evaluating. Each page includes pricing, AI capabilities, ownership model, and the
            scenarios where each tool wins.
          </p>
        </section>

        <section className="grid md:grid-cols-2 gap-4">
          {list.map((c) => (
            <Link key={c.slug} to={`/compare/${c.slug}`} data-testid={`compare-link-${c.slug}`}>
              <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition h-full">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] tracking-widest">{c.type}</Badge>
                    <GitCompare className="w-4 h-4 text-zinc-600" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">getbitsy.ai vs</p>
                    <p className="text-2xl font-semibold mt-1">{c.name}</p>
                  </div>
                  <p className="text-zinc-300 text-sm leading-relaxed">{c.tagline_ours}</p>
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                    <div className="flex gap-6 text-xs">
                      <div>
                        <p className="text-zinc-500">{c.name}</p>
                        <p className="text-rose-400 font-mono">{c.headline_metric.them}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">getbitsy</p>
                        <p className="text-emerald-400 font-mono">{c.headline_metric.us}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-zinc-500" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </section>

        <section className="border-t border-zinc-800 pt-10">
          <h2 className="text-2xl font-semibold mb-3">Looking for something else?</h2>
          <p className="text-zinc-400 leading-relaxed">
            See the full <Link to="/agents" className="text-amber-400 hover:underline">Hotel AI Agents</Link> lineup,
            the <Link to="/why-getbitsy" className="text-amber-400 hover:underline">interactive ROI calculator</Link>,
            or the <Link to="/docs" className="text-amber-400 hover:underline">technical documentation</Link>.
          </p>
        </section>
      </main>

      <footer className="border-t border-zinc-800/60 text-center py-6 text-[11px] text-zinc-600 tracking-wider">
        Powered by BHERMES · M.O.A.T. · <Link to="/" className="hover:text-zinc-400">getbitsy.ai</Link>
      </footer>
    </div>
  );
}
